/**
 * Job list (RM-118). A distinctive horizontal filter panel (multi-value selects
 * for status / render mode / job type; empty = all, each clears independently)
 * feeding a configurable {@link JobsTable}. Empty state + skeleton on first load.
 * Polls via useJobs (stops when all terminal).
 */
import { useMemo } from 'react'
import { useJobs } from '../hooks/useJobs'
import { useLocalStorage } from '../hooks/useLocalStorage'
import { useJobPreview, useNewJobSheet } from '../hooks/useJobPreview'
import { useAuth } from '../lib/auth'
import { Checkbox } from '../components/ui/checkbox'
import { hasMode, JOB_TYPE_LABELS } from '../lib/jobView'
import { TESTID } from '../lib/testids'
import type { Job, JobStatus, RenderMode } from '../lib/schemas'
import {
  Page,
  PageContent,
  PageHeader,
  PageHeaderIcon,
  PageHeaderTitle,
  PageHeaderActions,
} from '../components/Page'
import { JobListSkeleton } from '../components/Skeleton'
import { JobsTable } from '../components/jobs/JobsTable'
import { JobPreviewSheet } from '../components/jobs/JobPreviewSheet'
import { NewJobSheet } from '../components/jobs/NewJobSheet'
import { Button } from '../components/ui/button'
import { MultiSelect } from '../components/MultiSelect'
import {
  AlertCircle,
  Inbox,
  LayoutGrid,
  Plus,
  RefreshCw,
  SearchX,
  X,
} from 'lucide-react'

/** All job statuses, in lifecycle order, with a filter label + colour dot. */
const STATUS_OPTIONS: { value: JobStatus; label: string; dot: string }[] = [
  { value: 'preparing', label: 'Preparing', dot: 'bg-warning' },
  { value: 'queued', label: 'Queued', dot: 'bg-warning' },
  { value: 'provisioning', label: 'Provisioning', dot: 'bg-info' },
  { value: 'rendering', label: 'Rendering', dot: 'bg-info' },
  { value: 'uploading', label: 'Uploading', dot: 'bg-info' },
  { value: 'done', label: 'Done', dot: 'bg-success' },
  { value: 'delivering', label: 'Delivering', dot: 'bg-info' },
  { value: 'delivered', label: 'Delivered', dot: 'bg-success' },
  { value: 'delivery_failed', label: 'Delivery Failed', dot: 'bg-destructive' },
  { value: 'failed', label: 'Failed', dot: 'bg-destructive' },
  { value: 'cancelled', label: 'Cancelled', dot: 'bg-muted-foreground' },
]

const MODE_OPTIONS: { value: RenderMode; label: string }[] = [
  { value: 'animation', label: 'Animation' },
  { value: 'tour', label: 'Tour' },
  { value: 'turntable', label: 'Turntable' },
  { value: 'topview', label: 'TopView' },
]

/** Job-type (job_spec.type) filter options. */
type JobKind = Job['job_spec']['type']
const KIND_OPTIONS = Object.entries(JOB_TYPE_LABELS).map(([value, label]) => ({
  value: value as JobKind,
  label,
}))

/** The active filter selection. Empty arrays = no filter (all pass). */
interface JobFilters {
  status: JobStatus[]
  mode: RenderMode[]
  kind: JobKind[]
}

/**
 * Persisted filter state — a single versioned object (RM-118). Kept as one blob
 * so a future "named quick filters" feature can grow it to
 * `{ v, active, saved: [{ name, filters }] }` without a storage migration.
 */
interface FiltersState {
  v: 1
  active: JobFilters
}

const EMPTY_FILTERS: JobFilters = { status: [], mode: [], kind: [] }
const DEFAULT_FILTERS: FiltersState = { v: 1, active: EMPTY_FILTERS }

/** Coalesce a persisted (possibly stale/partial) blob into a valid selection. */
function normalizeActive(state: FiltersState | undefined): JobFilters {
  const a = state?.active
  return {
    status: Array.isArray(a?.status) ? a.status : [],
    mode: Array.isArray(a?.mode) ? a.mode : [],
    kind: Array.isArray(a?.kind) ? a.kind : [],
  }
}

export function JobListPage() {
  const { preview, open, close } = useJobPreview()
  const newJob = useNewJobSheet()
  const auth = useAuth()
  const isAdmin = auth.status === 'authenticated' && auth.user.is_system_admin
  // Admin-only "show all jobs" toggle. Kept in its own storage key (not the
  // filters blob) and re-gated by `isAdmin` on read, so a persisted `true` can
  // never leak other users' jobs to a user who has since lost admin (the server
  // enforces this too — the flag is UX only).
  const [showAllRaw, setShowAll] = useLocalStorage<boolean>(
    'rm.jobs.showAll',
    false,
  )
  const showAll = isAdmin && showAllRaw
  const { data, error, isFetching, refetch } = useJobs(100, showAll)
  const [filtersState, setFiltersState] = useLocalStorage<FiltersState>(
    'rm.jobs.filters',
    DEFAULT_FILTERS,
  )
  const {
    status: statusFilters,
    mode: modeFilters,
    kind: kindFilters,
  } = normalizeActive(filtersState)

  const allJobs = useMemo<Job[]>(() => data?.jobs ?? [], [data])
  const count = (s: JobStatus) => allJobs.filter((j) => j.status === s).length

  // Empty selection = no filter (all pass). Memoized so the table's pagination
  // survives 3s polls (react-query structural sharing keeps `allJobs` stable
  // when nothing changed) and only resets when filters/content actually change.
  const filtered = useMemo(
    () =>
      allJobs
        .filter(
          (j) => statusFilters.length === 0 || statusFilters.includes(j.status),
        )
        .filter(
          (j) =>
            modeFilters.length === 0 || modeFilters.some((m) => hasMode(j, m)),
        )
        .filter(
          (j) =>
            kindFilters.length === 0 ||
            kindFilters.includes(j.job_spec.type as JobKind),
        ),
    [allJobs, statusFilters, modeFilters, kindFilters],
  )

  // `data` is authoritative once the first poll resolves — no artificial grace,
  // so the skeleton clears the instant the list responds (empty or not).
  const isLoading = !data && !error
  const hasInitialError = !data && !!error
  // No jobs at all → first-run empty state (filters hidden). Jobs exist but the
  // active filters exclude everything → a distinct "no matches" state.
  const isEmpty = !!data && allJobs.length === 0
  const noResults = !isLoading && !isEmpty && filtered.length === 0
  // Admins always get the filter panel (even with an empty own-list) so the
  // "show all jobs" toggle stays reachable; otherwise it's hidden with nothing
  // to filter.
  const showFilters = !isLoading && (!isEmpty || isAdmin)
  const anyFilter =
    statusFilters.length > 0 || modeFilters.length > 0 || kindFilters.length > 0

  const statusOptions = STATUS_OPTIONS.map((o) => ({
    value: o.value,
    label: o.label,
    dot: o.dot,
    count: count(o.value),
    testId: TESTID.filter(o.value),
  }))
  const modeOptions = MODE_OPTIONS.map((o) => ({
    value: o.value,
    label: o.label,
    testId: TESTID.modeFilter(o.value),
  }))
  const kindOptions = KIND_OPTIONS.map((o) => ({
    value: o.value,
    label: o.label,
    testId: TESTID.typeFilter(o.value),
  }))

  const patchActive = (patch: Partial<JobFilters>) =>
    setFiltersState((s) => ({
      v: 1,
      active: { ...normalizeActive(s), ...patch },
    }))
  const setStatus = (v: string[]) => patchActive({ status: v as JobStatus[] })
  const setMode = (v: string[]) => patchActive({ mode: v as RenderMode[] })
  const setKind = (v: string[]) => patchActive({ kind: v as JobKind[] })
  const clearAll = () => patchActive(EMPTY_FILTERS)

  return (
    <Page>
      <PageHeader>
        <PageHeaderIcon>
          <LayoutGrid />
        </PageHeaderIcon>
        <PageHeaderTitle data-testid={TESTID.jobListHeading}>
          Render Jobs
        </PageHeaderTitle>
        <PageHeaderActions>
          <Button
            size="lg"
            className="shadow-sm"
            data-testid={TESTID.newJobButton}
            onClick={newJob.open}
          >
            <Plus className="size-5" />
            Nowy render
          </Button>
        </PageHeaderActions>
      </PageHeader>

      <PageContent className="space-y-4">
        {/* Filter panel — hidden when there are no jobs at all (nothing to filter);
          stacked full-width on mobile, wraps horizontally from sm. */}
        {showFilters && (
          <div className="flex flex-col gap-3 rounded-xl border border-border bg-card p-3 sm:flex-row sm:flex-wrap sm:items-center">
            <span className="shrink-0 text-sm font-medium text-muted-foreground">
              Filtry
            </span>
            <FilterGroup
              active={statusFilters.length > 0}
              onClear={() => setStatus([])}
            >
              <MultiSelect
                value={statusFilters}
                onChange={setStatus}
                options={statusOptions}
                allLabel="Wszystkie statusy"
                triggerTestId={TESTID.statusFilterSelect}
                className="flex-1 sm:flex-none sm:min-w-44"
              />
            </FilterGroup>
            <FilterGroup
              active={modeFilters.length > 0}
              onClear={() => setMode([])}
            >
              <MultiSelect
                value={modeFilters}
                onChange={setMode}
                options={modeOptions}
                allLabel="Wszystkie tryby"
                triggerTestId={TESTID.modeFilterSelect}
                className="flex-1 sm:flex-none sm:min-w-40"
              />
            </FilterGroup>
            <FilterGroup
              active={kindFilters.length > 0}
              onClear={() => setKind([])}
            >
              <MultiSelect
                value={kindFilters}
                onChange={setKind}
                options={kindOptions}
                allLabel="Wszystkie typy"
                triggerTestId={TESTID.typeFilterSelect}
                className="flex-1 sm:flex-none sm:min-w-40"
              />
            </FilterGroup>
            {anyFilter && (
              <Button variant="ghost" size="sm" onClick={clearAll}>
                Wyczysc wszystko
              </Button>
            )}
            {isAdmin && (
              <label className="ml-auto flex shrink-0 cursor-pointer items-center gap-2 text-sm text-muted-foreground">
                <Checkbox
                  checked={showAll}
                  onCheckedChange={(checked) => setShowAll(checked === true)}
                  data-testid="jobs-show-all"
                />
                Wszystkie joby (admin)
              </label>
            )}
          </div>
        )}

        {/* Content */}
        {isLoading ? (
          <JobListSkeleton count={6} />
        ) : hasInitialError ? (
          <div
            className="flex flex-col items-center py-16 text-center"
            data-testid={TESTID.jobListError}
          >
            <AlertCircle className="mb-3 size-12 text-destructive" />
            <h3 className="mb-2 text-lg font-medium text-foreground">
              Nie udalo sie pobrac renderow
            </h3>
            <p className="mb-6 text-muted-foreground">
              Sprobuj ponownie. Jesli problem sie powtarza, zglos go
              administratorowi.
            </p>
            <Button
              variant="outline"
              onClick={() => void refetch()}
              disabled={isFetching}
              data-testid={TESTID.jobListRetry}
            >
              <RefreshCw className={isFetching ? 'animate-spin' : undefined} />
              Sprobuj ponownie
            </Button>
          </div>
        ) : isEmpty ? (
          <div className="text-center py-16" data-testid={TESTID.emptyState}>
            <Inbox className="mx-auto mb-3 size-12 text-muted-foreground" />
            <h3
              className="text-lg font-medium text-foreground mb-2"
              data-testid={TESTID.emptyStateMessage}
            >
              Nie masz jeszcze zadnych renderow
            </h3>
            <p className="text-muted-foreground mb-6">
              Wyslij swoja pierwsza scene do renderingu w chmurze
            </p>
            <div className="flex flex-col items-center gap-3">
              <Button
                size="lg"
                className="shadow-sm"
                data-testid={TESTID.emptyStateCta}
                onClick={newJob.open}
              >
                <Plus className="size-5" />
                Nowy render
              </Button>
              <a
                className="text-sm text-muted-foreground hover:text-foreground hover:underline"
                href="#"
                data-testid={TESTID.blenderPluginLink}
              >
                Pobierz plugin do Blendera
              </a>
            </div>
          </div>
        ) : noResults ? (
          <div
            className="text-center py-16"
            data-testid={TESTID.filterNoResults}
          >
            <SearchX className="mx-auto mb-3 size-12 text-muted-foreground" />
            <h3 className="text-lg font-medium text-foreground mb-2">
              Brak zadan dla wybranych filtrow
            </h3>
            <p className="text-muted-foreground mb-6">
              Zaden job nie pasuje do aktualnie wybranych filtrow.
            </p>
            <Button variant="outline" onClick={clearAll}>
              Wyczysc filtry
            </Button>
          </div>
        ) : (
          <JobsTable
            jobs={filtered}
            resetKey={[
              String(showAll),
              ...statusFilters,
              ...modeFilters,
              ...kindFilters,
            ].join(',')}
            onRowClick={open}
          />
        )}
      </PageContent>

      <JobPreviewSheet jobId={preview} onClose={close} />
      <NewJobSheet open={newJob.isOpen} onClose={newJob.close} />
    </Page>
  )
}

/** A filter select paired with a per-filter clear (×) shown only when active. */
function FilterGroup({
  active,
  onClear,
  children,
}: {
  active: boolean
  onClear: () => void
  children: React.ReactNode
}) {
  return (
    <div className="flex items-center gap-1">
      {children}
      {active && (
        <Button
          variant="ghost"
          size="icon-sm"
          aria-label="Wyczysc filtr"
          onClick={onClear}
        >
          <X />
        </Button>
      )}
    </div>
  )
}
